import { QuestionData, QuestionOption } from 'src/types/formQuestionnaire';
import { QuestionAnswer } from '../FormPreview.reducer';

export type NextStep =
  | { type: 'next' }
  | { type: 'submit' }
  | { type: 'question'; questionId: string };

const parseNextAction = (nextAction: string | undefined): NextStep => {
  if (!nextAction || nextAction === 'next') return { type: 'next' };
  if (nextAction === 'submit') return { type: 'submit' };
  if (nextAction.startsWith('question:')) {
    return {
      type: 'question',
      questionId: nextAction.slice('question:'.length),
    };
  }
  return { type: 'next' };
};

const getSelectedOptions = (
  question: QuestionData,
  answer: QuestionAnswer | undefined,
): QuestionOption[] => {
  if (!answer || !question.options?.length) return [];

  const byId = new Map(question.options.map((opt) => [opt.id, opt]));
  const selectedIds: string[] = [];

  if (answer.radioOption) selectedIds.push(answer.radioOption);
  if (answer.singleDropdownOption) {
    selectedIds.push(String(answer.singleDropdownOption.value));
  }
  if (answer.checkboxOptions?.length)
    selectedIds.push(...answer.checkboxOptions);
  if (answer.multiDropdownOptions?.length) {
    selectedIds.push(
      ...answer.multiDropdownOptions.map((o) => String(o.value)),
    );
  }

  return selectedIds
    .map((id) => byId.get(id))
    .filter((opt): opt is QuestionOption => Boolean(opt));
};

/**
 * A question is only "answered" for branching purposes once its selection is
 * known; before that we assume the sequential path so unanswered questions
 * still count toward the total.
 */
export const resolveNextStep = (
  question: QuestionData,
  answer: QuestionAnswer | undefined,
): NextStep => {
  const selectedOptions = getSelectedOptions(question, answer);
  if (selectedOptions.length === 0) return { type: 'next' };

  const nextSteps = selectedOptions.map((opt) =>
    parseNextAction(opt.nextAction),
  );
  if (nextSteps.some((step) => step.type === 'submit')) {
    return { type: 'submit' };
  }

  const jumpSteps = nextSteps.filter((step) => step.type === 'question');
  if (jumpSteps.length > 0) return jumpSteps[0];

  return { type: 'next' };
};

/**
 * Walks the question list from the start, following each answered question's
 * branching choice, to produce the list of questions reachable given the
 * answers collected so far. Unanswered questions default to the sequential
 * path, so the total only shrinks once a skip is actually chosen.
 */
export const resolveReachableQuestions = (
  questions: QuestionData[],
  answers: Record<string, QuestionAnswer>,
): QuestionData[] => {
  if (questions.length === 0) return [];

  const indexById = new Map(questions.map((q, index) => [q.id, index]));
  const reachable: QuestionData[] = [];
  const visited = new Set<number>();

  let index = 0;
  while (index >= 0 && index < questions.length && !visited.has(index)) {
    visited.add(index);
    const question = questions[index];
    reachable.push(question);

    const nextStep = resolveNextStep(question, answers[question.id]);
    if (nextStep.type === 'submit') break;
    if (nextStep.type === 'next') {
      index += 1;
    } else {
      const targetIndex = indexById.get(nextStep.questionId);
      index = targetIndex ?? index + 1;
    }
  }

  return reachable;
};
